import { useEffect, useState } from "react";
import SearchCard from "../../components/cards/SearchCard";
import {
  UniversityIcon,
  ShareIcon,
  SaveIcon,
  ActionIcon,
  ArrowRightAltIcon,
} from "../../common/icons";
import { getScholarshipsReq } from "../../utils/api";
import {
  courseCardData,
  type ScholarshipData,
} from "../../content/scholarship-course-page";
import SearchResultsSkeleton from "../../components/skeleton/SearchResultsSkeleton";
import { CompareModal, type Scholarship } from "../modal-button/Compare";
import SectionHeading from "../../components/heading/SectionHeading";

interface CardsListSectionProps {
  onCompareClick?: (scholarship: Scholarship) => void;
  scholarship_type?: { id: number; display_name: string }[];
  institute_slug?: string;
}

const CardListSection = ({
  onCompareClick,
  scholarship_type,
  institute_slug,
}: CardsListSectionProps = {}) => {
  const [searchParams] = useState<URLSearchParams>(
    new URLSearchParams(window.location.search),
  );
  const [visibleCount, setVisibleCount] = useState(4);

  const [courses, setCourses] = useState<ScholarshipData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [selectedScholarships, setSelectedScholarships] = useState<
    Scholarship[]
  >([]);

  const { defaultImage, labels } = courseCardData;
  const truncateText = (text: string, limit: number) => {
    if (text.length <= limit) return text;
    return `${text.slice(0, limit)}...`;
  };
  const getCourses = async () => {
    try {
      setLoading(true);

      const totalPages = 50;
      const promises = [];

      // Create an array of promises for each page request
      for (let page = 1; page <= totalPages; page++) {
        const params = Object.fromEntries(searchParams.entries());
        params.page = page.toString();
        promises.push(getScholarshipsReq(params));
      }

      const results = await Promise.all(promises);

      const allCourses = results.flatMap((data) => data.results || []);

      const filteredCourses = scholarship_type
        ? allCourses.filter((course: ScholarshipData) =>
            course.scholarship_type.some((type) =>
              scholarship_type.some(
                (st) => st.display_name === type.display_name,
              ),
            ),
          )
        : allCourses;

      setCourses(filteredCourses);
    } catch (err) {
      console.error("Error fetching courses:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCourses([]);
    getCourses();
  }, [searchParams, scholarship_type]);
  const handleCompareClick = (scholarship: ScholarshipData) => {
    if (onCompareClick) {
      const formattedScholarship: Scholarship = {
        id: scholarship.id,
        scholarship_name: scholarship.scholarship_name || "Unknown Scholarship",
        institute: {
          id: scholarship.institute?.id || 0,
          name: scholarship.institute?.institute_name || "Unknown Institute",
          logo: scholarship.institute?.logo || defaultImage,
        },
        slug: scholarship.slug || "",
      };
      onCompareClick(formattedScholarship);
      return;
    }

    const scholarshipToAdd: Scholarship = {
      id: scholarship.id,
      scholarship_name: scholarship.scholarship_name || "Unknown Scholarship",
      institute: {
        id: scholarship.institute?.id || 0,
        name: scholarship.institute?.institute_name || "Unknown Institute",
        logo: scholarship.institute?.logo || defaultImage,
      },
      slug: scholarship.slug || "",
    };

    const isDuplicate = selectedScholarships.some(
      (sch) => sch.id === scholarshipToAdd.id,
    );

    if (isDuplicate) {
      alert("This scholarship is already selected for comparison");
      return;
    }

    if (selectedScholarships.length >= 3) {
      alert("You can compare maximum 3 scholarships");
      return;
    }

    setSelectedScholarships([...selectedScholarships, scholarshipToAdd]);
    setIsCompareModalOpen(true);
  };

  const getCardConfig = (course: ScholarshipData) => ({
    icon: UniversityIcon,
    pageType: "scholarship" as const,
    detailsConfig: [
      {
        label: labels.degreeLevel,
        value: course.degree_level
          .map((level: { name: string }) => level.name)
          .join(", "),
        key: "degree",
      },
      {
        label: labels.type,
        value:
          course.scholarship_type
            .map((type: { display_name: string }) => type.display_name)
            .join(", ") || "N/A",
        key: "type",
      },
      {
        label: labels.value,
        value: truncateText(course.scholarship_value || "N/A", 10) || "N/A",
        key: "value",
      },
      {
        label: labels.intake,
        value:
          course.intakes
            .map((item: { year: number }) => item.year)
            .join(", ") || "N/A",
        key: "intake",
      },
    ],
    buttons: {
      primary: {
        label: "Apply now",
        href: "https://timesconsultant.com/contact/",
      },
      secondary: {
        label: labels.viewDetail,
        href: `/scholarship/${course.slug}`,
      },
    },
    actionIcons: [SaveIcon, ActionIcon, ShareIcon],
  });

  if (loading && courses.length === 0) {
    return <SearchResultsSkeleton numberOfCards={3} />;
  }

  return (
    <div>
      {courses.length > 0 && (
        <>
          <div className="px-[30px] sm:px-[20px] max-w-7xl mx-auto w-full">
            <SectionHeading title="Explore more scholarships" />
          </div>
          {courses.slice(0, visibleCount).map((course, i) => (
            <SearchCard
              handleCompareClick={() => handleCompareClick(course)}
              key={`${course.id || i}-${i}`}
              data={{
                name: course.scholarship_name || "Unknown",
                institute_name: course.institute?.institute_name || "N/A",
                logo: course.institute?.logo || defaultImage,
                slug: course.slug,
                type: "scholarship",
                id: course.id,
                institute_slug: institute_slug,
              }}
              config={getCardConfig(course)}
              defaultImage={defaultImage}
              gridClassName="grid-cols-3 xl:grid-cols-4"
              variant="uni-detail"
            />
          ))}
          {visibleCount < courses.length && (
            <div className="mt-4 flex justify-end px-2">
              <span
                onClick={() => setVisibleCount(courses.length)}
                className="flex items-center cursor-pointer text-primary gap-1 "
              >
                {" "}
                <ArrowRightAltIcon />
                See More
              </span>
            </div>
          )}
        </>
      )}
      {!onCompareClick && (
        <CompareModal
          open={isCompareModalOpen}
          onClose={() => setIsCompareModalOpen(false)}
          selectedEntities={selectedScholarships}
          entityType="scholarship"
          setSelectedEntities={setSelectedScholarships}
        />
      )}
    </div>
  );
};

export default CardListSection;
